import React from 'react';
import { z } from 'zod';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Article detail header — faithful rewrite of legacy molecules/InsightsInnerSection
// (the header of an insight/news/solution-brief detail page): read-time + date,
// a large black title, and the centered feature image. The markdown body is
// rendered by the privacyContent block that follows.
export const articleDetailSchema = z.object({
  minuteRead: z.string().optional(),
  dateLabel: z.string().optional(),
  title: z.string().min(1),
  imageUrl: z.string().optional(),
});

type ArticleDetailProps = z.infer<typeof articleDetailSchema>;

const Section = styled.section`
  max-width: 112rem;
  margin: 0 auto;
  padding: 3rem 1.6rem 0;

  @media (min-width: 48em) {
    padding: 6rem 1.6rem 0;
  }
`;

const Meta = styled.p`
  margin: 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
  font-size: 1.5rem;
`;

const Title = styled.h1`
  margin: 1.6rem 0 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: 2.6rem;
  line-height: 1.2;

  @media (min-width: 48em) {
    font-size: 5rem;
  }
`;

const ImageWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 3rem;
`;

const Img = styled.img`
  max-width: 100%;
  max-height: 42rem;
  border-radius: 1rem;
  object-fit: contain;
`;

function ArticleDetail({ minuteRead, dateLabel, title, imageUrl }: ArticleDetailProps) {
  const meta = [minuteRead ? `${minuteRead} min read` : null, dateLabel || null].filter(Boolean).join(' | ');
  return (
    <Section>
      {meta ? <Meta>{meta}</Meta> : null}
      <Title>{title}</Title>
      {imageUrl ? (
        <ImageWrap>
          <Img src={imageUrl} alt="" />
        </ImageWrap>
      ) : null}
    </Section>
  );
}

export const articleDetailBlock: BlockDefinition<ArticleDetailProps> = {
  type: 'articleDetail',
  schema: articleDetailSchema,
  Renderer: ArticleDetail,
};
